import { useEffect, useRef, useState } from 'react';
import { getDistance } from 'geolib';

export const SETTLE_MS = 2 * 60 * 1000;
export const SETTLE_RADIUS_M = 30;

type Coordinates = [lng: number, lat: number];

/**
 * Returns the first known position right away, and after that a new position once it stayed within SETTLE_RADIUS_M for
 * SETTLE_MS, so lookups don't follow every GPS update.
 * The result only changes when the settled spot is further than SETTLE_RADIUS_M from the previous one.
 */
export const useSettledPosition = (lng: number | undefined, lat: number | undefined): Coordinates | null => {

  const [settled, setSettled] = useState<Coordinates | null>(null);
  const settledRef = useRef<Coordinates | null>(null);
  const anchor = useRef<Coordinates | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {

    if (lng === undefined || lat === undefined) return;

    const here: Coordinates = [lng, lat];

    // still at the same spot: keep the running timer
    if (anchor.current && getDistance(anchor.current, here) <= SETTLE_RADIUS_M) return;

    const first = !anchor.current;

    anchor.current = here;
    clearTimeout(timer.current);

    // opening the alarm: we can't tell how long they have been there, so look up right away; the wait only applies to moves after that
    if (first) {

      settledRef.current = here;
      setSettled(here);

      return;

    }

    timer.current = setTimeout(() => {

      // within the radius of the settled spot: nothing changes, and the anchor goes back to it, so a later fix outside
      // the radius starts a new settle instead of being compared with this drifted anchor
      if (settledRef.current && getDistance(settledRef.current, here) <= SETTLE_RADIUS_M) {

        anchor.current = settledRef.current;

        return;

      }

      settledRef.current = here;
      setSettled(here);

    }, SETTLE_MS);

  }, [lng, lat]);

  useEffect(() => () => clearTimeout(timer.current), []);

  return settled;

};
